import React, { useState } from 'react'
import {
  Box,
  IconButton,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import MuiAppBar from '@mui/material/AppBar'
import MenuIcon from '@mui/icons-material/Menu'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import Logout from '@mui/icons-material/Logout'
import { useNavigate } from 'react-router-dom'
import profilePhoto from '../../assets/profile-icon.png'
import { useSelector, useDispatch } from 'react-redux'

const drawerWidth = 240

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

const AppBarComponent = ({ open, handleDrawerOpen }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState(null)

  const handleMenuToggle = (event) => {
    if (anchorEl) {
      setAnchorEl(null)
    } else {
      setAnchorEl(event.currentTarget)
    }
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    dispatch({ type: 'RESET' })
    navigate('/')
  }

  const handleChangePasswordClick = () => {
    handleMenuClose()
    navigate('/change')
  }

  const handleProfileClick = () => {
    handleMenuClose()
    navigate('/profile')
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)

    const day = date.getDate()
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ]
    const month = monthNames[date.getMonth()]
    const year = date.getFullYear()

    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    const seconds = date.getSeconds().toString().padStart(2, '0')

    const daySuffix = (day) => {
      if (day > 3 && day < 21) return 'th'
      switch (day % 10) {
        case 1:
          return 'st'
        case 2:
          return 'nd'
        case 3:
          return 'rd'
        default:
          return 'th'
      }
    }

    return `${day}${daySuffix(
      day
    )} ${month} ${year} ${hours}:${minutes}:${seconds}`
  }

  const userDetails = useSelector(
    (state) => state?.user?.data?.data?.userLoginDetailModel
  )

  const formattedLastLoginDate = userDetails?.lastLoginDate
    ? formatDate(userDetails.lastLoginDate)
    : 'No last login date available'

  return (
    <AppBar
      position='fixed'
      open={open}
      sx={{ backgroundColor: '#FFFFFF', boxShadow: 'none' }}>
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: '100px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            color='inherit'
            aria-label='open drawer'
            onClick={handleDrawerOpen}
            edge='start'
            sx={{
              marginRight: 5,
              color: '#F8764D',
              ...(open && { display: 'none' }),
            }}>
            <MenuIcon />
          </IconButton>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              marginTop: 2.5,
              padding: 0,
            }}>
            <Typography
              variant='h6'
              noWrap
              component='div'
              color='textPrimary'
              sx={{ fontWeight: 700 }}>
              Welcome {userDetails?.name}!
            </Typography>
            <Typography variant='caption' color='textSecondary'>
              Last Login: {formattedLastLoginDate}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            alt='Profile Image'
            src={profilePhoto}
            sx={{ width: 56, height: 56 }}
          />
          <Box sx={{ ml: 2 }}>
            <Typography
              variant='body1'
              sx={{ fontWeight: 'bold', color: 'black' }}>
              {userDetails?.userName}
            </Typography>
            <Typography
              variant='subtitle2'
              sx={{
                fontWeight: 100,
                fontSize: '0.875rem',
                letterSpacing: '0.1em',
                color: 'black',
              }}>
              {userDetails?.designation}
            </Typography>
          </Box>
          <IconButton onClick={handleMenuToggle}>
            <ArrowDropDownIcon
              fontSize='large'
              sx={{
                color: '#F8764D',
                transition: 'transform 0.3s',
                transform: anchorEl ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}>
              <MenuItem onClick={handleProfileClick}>Profile</MenuItem>
              <MenuItem onClick={handleChangePasswordClick}>
                Change Password
              </MenuItem>
            </Menu>
          </IconButton>
          <IconButton onClick={handleLogout}>
            <Logout sx={{ color: '#F8764D' }} />
          </IconButton>
        </Box>
      </Toolbar>
    </AppBar>
  )
}

export default AppBarComponent
